import classNames from "classnames";
import React, { useEffect, useState, useRef, ChangeEvent, useImperativeHandle, useMemo } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader, Button, SpinningButton } from "Components/ReactstrapSlim";
import { useApiClient } from "Services/API/useApiClient";
import { PictureClient, SaveImageResponse } from "Services/LegacyAPI/PictureClient";
import AvatarEditor, { ImageState } from "react-avatar-editor";
import * as Icons from "react-feather";
import LsLoading from "Components/LsLoading";

export interface ImageUploadHandle {
   wrapperClassName: string,
   pictureUrl: string,
   edit: () => void,
   delete: () => void,
}

export interface ImageUploadProps {
   pictureId?: Guid,
   pictureOwnerId: Guid,
   pictureDbOwnerParentId?: Guid,
   pictureType: LSCommon.Enums.PictureTypeEnum,
   pictureTag: LSCrossTier.ImageTagConstants,
   width: number,
   height: number,
   crop?: boolean,

   roundedBorder?: boolean,

   uploadModalTitle?: string,
   editModalTitle?: string,
   deleteModalTitle?: string,

   onPictureUrlChanged?: (pictureUrl: string) => void,
   onImageUploaded?: (data: SaveImageResponse) => void,
   onImageRemoved?: () => void,
}

export const ImageUpload = React.forwardRef<ImageUploadHandle, ImageUploadProps>((props, ref) => {
   const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
   const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
   const [isSaving, setIsSaving] = useState(false);
   const [scale, setScale] = useState(1);
   const [rotate, setRotate] = useState(0);
   const [pictureId, setPictureId] = useState<Guid | undefined>(props.pictureId);
   const editor = useRef<AvatarEditor>(null);
   const pictureClient = useApiClient(PictureClient);
   const fileInputRef = useRef<HTMLInputElement>(null);
   const [file, setFile] = useState<File | null>(null);
   const [imgInfo, setImgInfo] = useState<{ width: number, height: number, name: string, type: string } | null>(null);
   const [imgState, setImgState] = useState<ImageState | null>(null);
   const [isEdit, setIsEdit] = useState<boolean>(false);
   const [lastSavedTimeStamp, setLastSavedTimeStamp] = useState<Date>(new Date());

   const getPictureUrl = (skipCdn?: boolean) => {
      let result = "";
      if (pictureId) {
         const skipTimestamp = props.pictureTag == LSCrossTier.ImageTagConstants.UserPicture;
         const cdnEndpoint = skipCdn ? "" : LiquidSpaceConfig.CDNConfiguration.Picture;
         result = (cdnEndpoint ? cdnEndpoint : GetUrl("Index", "Picture")) + "?maxWidth="
            + props.width + "&maxHeight=" + props.height + "&crop=" + (props.crop || false) + "&id=" + pictureId + (skipTimestamp ? "" : "&timestamp=" + lastSavedTimeStamp.getTime());
      }
      return result;
   }

   const pictureUrl = useMemo<string>(() => {
      const result = getPictureUrl();
      return result;
   }, [pictureId, lastSavedTimeStamp, props.width, props.height, props.crop, props.pictureType]);


   const ev: any[] = [];
   let prevDiff = -1;



   const onClearStates = () => {
      setFile(null);
      setRotate(0);
      setScale(1);
      setImgInfo(null);
      setImgState(null);
      setIsEdit(false);
   }

   const onWheel = (e: any) => {
      if (!isSaving) {
         setScale((prevScale) => {
            return Math.min(Math.max(.125, prevScale + e.deltaY * -0.001), 4);
         })
      }
   }

   const onPointerEvent = (e: any) => {
      // Remove this pointer from the cache
      for (let i = 0; i < ev.length; i++) {
         if (ev[i].pointerId == e.pointerId) {
            ev.splice(i, 1);
            break;
         }
      }

      // If the number of pointers down is less than two then reset diff tracker
      if (ev.length < 2) {
         prevDiff = -1;
      }
   }

   const onPointerDown = (e: any) => {
      ev.push(e);
   }

   const onPointerMove = (e: any) => {
      // Find this event in the cache and update its record with this event
      for (let i = 0; i < ev.length; i++) {
         if (e.pointerId == ev[i].pointerId) {
            ev[i] = e;
            break;
         }
      }

      // If two pointers are down, check for pinch gestures
      if (ev.length === 2) {
         // Calculate the distance between the two pointers
         const curDiff = Math.abs(ev[0].clientX - ev[1].clientX);
         if (prevDiff > 0) {
            if (curDiff > prevDiff) {
               // The distance between the two pointers has increased
               setScale((prevScale) => {
                  return Math.min(Math.max(.125, prevScale + curDiff * 0.1), 4);
               })
            }
            if (curDiff < prevDiff) {
               // The distance between the two pointers has decreased
               setScale((prevScale) => {
                  return Math.min(Math.max(.125, prevScale - curDiff * 0.1), 4);
               })
            }
         }

         // Cache the distance for the next move event
         prevDiff = curDiff;
      }
   }

   const toggleModal = () => {
      setIsModalOpen((prevState) => {
         return !prevState;
      })
   }

   const toggleIsDeleteModal = () => {
      setIsDeleteOpen((prevState) => {
         return !prevState;
      })
   }

   const onRotate = (angle: number) => {
      setRotate((prevRotate) => {
         return prevRotate + angle
      })
   }

   const onScale = (scaleDelta: number) => {
      setScale((prevScale) => {
         return Math.min(Math.max(.125, prevScale + scaleDelta), 4);
      })
   }

   const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
      setIsSaving(true);
      const loadedFile = e.target.files?.item(0) || null;
      setRotate(0);
      setScale(1);

      if (loadedFile) {
         setFile(loadedFile);
         const img: HTMLImageElement = document.createElement("img");

         img.onload = function () {
            setImgInfo({ width: img.width, height: img.height, name: loadedFile.name, type: loadedFile.type });
         };

         img.src = URL.createObjectURL(loadedFile);
         toggleModal();
      }

      e.target.value = "";
   }

   const onSave = async () => {
      setIsSaving(true);
      const abortController = new AbortController();

      try {
         if (editor && editor.current && imgState && (file || pictureUrl)) {
            // let croppingRect = editor.current.getCroppingRect();

            // let width = imgInfo ? imgInfo.width : imgState.width;
            // let height = imgInfo ? imgInfo.height : imgState.height;

            // let cropRequest: CropRequest = {
            //     ImgW: width,
            //     ImgH: height,
            //     ImgY1: croppingRect.y * width,
            //     ImgX1: croppingRect.x * height,
            //     CropH: croppingRect.height * height,
            //     CropW: croppingRect.width * width,
            //     Angle: rotate,
            //     RotateAfterCrop: true
            // }

            /*
            const dataUrl = editor.current.getImage().toDataURL(imgInfo ? imgInfo.type : "image/png");
            const res = await fetch(dataUrl);
            const blob = await res.blob();
            /*/
            const canvas = editor.current.getImage();
            const blobPromise = new Promise<Blob>((resolve, reject) => canvas.toBlob(blob => (blob ? resolve(blob) : reject("no blob")), "image/jpeg"));
            const blob = await blobPromise;
            //*/
            const f = new File([blob], imgInfo ? imgInfo.name : `image-${pictureId}`, { type: imgInfo ? imgInfo.type : "image/png" });
            const result = await pictureClient.saveImage(props.pictureOwnerId,
               f,
               props.pictureType,
               LSCrossTier.ImageTagConstants.describe(props.pictureTag),
               pictureId,
               undefined,
               undefined,
               props.pictureDbOwnerParentId);

            if (result.success) {
               //LSStore.instance.dispatch(LSStore.refreshUserThunk());
               //loadPictureData(abortController.signal);
               setLastSavedTimeStamp(new Date());
               setPictureId(result.id);
               toggleModal();
               onClearStates();
               if (props.onImageUploaded)
                  props.onImageUploaded(result);
            }
         }
      } catch (error) {
         console.log(error);
         abortController.abort();
      } finally {
         if (!abortController.signal.aborted) {
            setIsSaving(false);
         }
      }
   }

   const onDelete = async () => {
      setIsSaving(true);
      try {
         if (pictureId) {
            await pictureClient.deletePicture(props.pictureOwnerId, pictureId, props.pictureType, props.pictureDbOwnerParentId);
            setPictureId(undefined);
            //LSStore.instance.dispatch(LSStore.refreshUserThunk());
            onClearStates();
            toggleIsDeleteModal();
            if (props.onImageRemoved)
               props.onImageRemoved();
         }
      } catch (error) {
         console.log(error);
      } finally {
         setIsSaving(false);
      }
   }

   const onLoadImg = (imgInfo: ImageState) => {
      setImgState(imgInfo);
      setIsSaving(false);
   }

   const onPictureClick = () => {
      setIsSaving(true);
      setIsEdit(true);
      setFile(null);
      toggleModal();
   }

   useEffect(() => {
      setPictureId(props.pictureId);
   }, [props.pictureId])

   useEffect(() => {
      if (props.onPictureUrlChanged)
         props.onPictureUrlChanged(pictureUrl);
   }, [pictureUrl])

   useImperativeHandle(ref, () => ({
      wrapperClassName: "image-upload-wrapper",
      pictureUrl,
      edit: onPictureClick,
      delete: toggleIsDeleteModal,
   }));

   return (
      <>
         <input type="file" accept="image/jpeg, image/gif, image/png" size={5e6} className="image-upload-input" onChange={onFileChange} onClick={() => { setIsEdit(false) }} ref={fileInputRef} />
         <Modal isOpen={isDeleteOpen} toggle={toggleIsDeleteModal}>
            <ModalHeader className="h3" toggle={toggleIsDeleteModal}>Delete image</ModalHeader>
            <ModalBody className="ls-modal-body">
               {props.deleteModalTitle ?? "Are you sure you want to delete this image?"}
            </ModalBody>
            <ModalFooter>
               <Button color="secondary" onClick={toggleIsDeleteModal} >Cancel</Button>
               <SpinningButton isLoading={isSaving} onClick={onDelete} color="primary" size="md" >Delete</SpinningButton>
            </ModalFooter>
         </Modal>
         <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg" className="image-upload-modal">
            <ModalHeader className="h3" toggle={toggleModal}>{isEdit
               ? (props.editModalTitle ?? "Edit image")
               : (props.uploadModalTitle ?? "Upload new image")}
            </ModalHeader>
            <ModalBody className="image-upload-modal-body">
               <div className={classNames("image-editor-wrapper", { "reloading": isSaving })} onWheel={onWheel}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerEvent}
                  onPointerCancel={onPointerEvent}
                  onPointerOut={onPointerEvent}
                  onPointerLeave={onPointerEvent}>
                  {isSaving && <LsLoading isLoading={true} />}
                  <AvatarEditor
                     className="image-editor"
                     ref={editor}
                     image={file || getPictureUrl(true)}
                     width={520}
                     height={520}
                     border={0}
                     borderRadius={props.roundedBorder ? 500 : undefined}
                     color={[0, 41, 58, 0.5]} // RGBA
                     scale={scale}
                     rotate={rotate}
                     backgroundColor="white"
                     onLoadSuccess={onLoadImg}
                  />
               </div>
            </ModalBody>
            <ModalFooter className="image-upload-modal-footer">
               <div className="btn-wrapper-left">
                  <Button disabled={isSaving} fluid='sm' color="secondary" onClick={() => onRotate(-90)}><Icons.RotateCcw className="feather" /></Button>
                  <Button disabled={isSaving} fluid='sm' color="secondary" onClick={() => onRotate(90)}><Icons.RotateCw className="feather" /></Button>
               </div>
               <div className="btn-wrapper-middle">
                  <Button disabled={isSaving} fluid='sm' color="secondary" onClick={() => onScale(-0.1)}><Icons.ZoomOut className="feather" /></Button>
                  <Button disabled={isSaving} fluid='sm' color="secondary" onClick={() => onScale(0.1)}><Icons.ZoomIn className="feather" /></Button>
               </div>
               <div className="btn-wrapper-right">
                  <Button color="secondary" fluid='sm' onClick={toggleModal} >Cancel</Button>
                  <SpinningButton isLoading={isSaving} fluid='sm' onClick={onSave} color="primary" >Save</SpinningButton>
               </div>
            </ModalFooter>
         </Modal>
      </>
   )
});